import React, { useLayoutEffect, useRef } from 'react';
import { Grid, Label, Checkbox, Message } from 'semantic-ui-react';
import NumberInput from 'semantic-ui-react-numberinput';
import { describeYesNo, isNonNegativeInteger } from '../common/teamSettings';

const SettingsNumberInput = ({ label, value, disabled, placeholder, minValue = 0, onChange }) => {
    const container = useRef(null);

    const stepEmptyValue = (event, increase) => {
        if (disabled || (value != null && value !== '')) return;
        event.stopPropagation();
        event.preventDefault();
        onChange(String(increase ? Math.max(1, minValue) : minValue));
    };

    useLayoutEffect(() => {
        const input = container.current.querySelector('input');
        input.setAttribute('aria-label', label);
        input.disabled = disabled;
        const buttons = container.current.querySelectorAll('button');
        ['Decrease', 'Increase'].forEach((action, index) => {
            buttons[index].setAttribute('aria-label', `${action} ${label}`);
            buttons[index].setAttribute('title', `${action} ${label}`);
        });
    });

    return (
        <fieldset disabled={disabled} aria-label={`${label} stepper`} style={{ border: 0, padding: 0, margin: '0.5rem 0 0', minWidth: 0 }}>
            <div
                ref={container}
                onClickCapture={event => {
                    const button = event.target.closest('button');
                    if (button) stepEmptyValue(event, button.getAttribute('aria-label') === `Increase ${label}`);
                }}
                onKeyDownCapture={event => {
                    if (['ArrowUp', '+'].includes(event.key)) stepEmptyValue(event, true);
                    if (['ArrowDown', '-'].includes(event.key)) stepEmptyValue(event, false);
                }}
                onChangeCapture={event => {
                    if (event.target.value !== '' && (!isNonNegativeInteger(event.target.value) || Number(event.target.value) < minValue)) {
                        event.stopPropagation();
                    }
                }}
            >
                <NumberInput
                    minValue={minValue}
                    value={value == null ? '' : String(value)}
                    allowEmptyValue
                    placeholder={placeholder}
                    disabled={disabled}
                    onChange={nextValue => { if (!disabled) onChange(nextValue); }}
                />
            </div>
        </fieldset>
    );
};

const SettingRow = ({ label, children }) => (
    <Grid.Row>
        <Grid.Column width={4} textAlign="right">
            <Label size="large" active pointing="right">{label}</Label>
        </Grid.Column>
        <Grid.Column width={8}>{children}</Grid.Column>
    </Grid.Row>
);

export const TeamSettingsSection = ({ team, settings, staffingEnabled, onStaffingChange, ptoEnabled, onPtoChange, onChange, disabled = false }) => {
    const suppressNotifications = team.isActive === false ? true : settings.suppressNotifications;
    const {
        minimumMembersToBeReady,
        vacationLimit,
        submitReadiness,
    } = settings;

    return (
        <>
            <SettingRow label="Suppress notifications">
                <Checkbox
                    toggle
                    aria-label="Suppress notifications"
                    label={describeYesNo(suppressNotifications)}
                    checked={suppressNotifications}
                    disabled={disabled || team.isActive === false}
                    onChange={(_, d) => onChange({ suppressNotifications: d.checked })}
                />
                {team.isActive === false && (
                    <Message
                        warning
                        size="tiny"
                        style={{ marginTop: '0.5rem' }}
                        content="This team is inactive, so notifications are suppressed regardless of this value."
                    />
                )}
            </SettingRow>

            <SettingRow label="Minimum members to be ready">
                <Checkbox
                    toggle
                    aria-label="Minimum members to be ready"
                    label={staffingEnabled ? 'Required' : 'Not required'}
                    checked={staffingEnabled}
                    disabled={disabled}
                    onChange={(_, d) => onStaffingChange(d.checked)}
                />
                <SettingsNumberInput
                    label="Minimum members to be ready count"
                    minValue={staffingEnabled ? 1 : 0}
                    value={staffingEnabled && Number(minimumMembersToBeReady) === 0 ? '' : minimumMembersToBeReady}
                    disabled={disabled || !staffingEnabled}
                    onChange={value => onChange({ minimumMembersToBeReady: value })}
                />
            </SettingRow>

            <SettingRow label="Submit readiness">
                <Checkbox
                    toggle
                    aria-label="Submit readiness"
                    label={describeYesNo(submitReadiness)}
                    checked={submitReadiness}
                    disabled={disabled}
                    onChange={(_, d) => onChange({ submitReadiness: d.checked })}
                />
            </SettingRow>

            <SettingRow label="Daily PTO limit">
                <Checkbox
                    toggle
                    aria-label="Enable daily PTO limit"
                    label={ptoEnabled ? 'Limited' : 'Unlimited'}
                    checked={ptoEnabled}
                    disabled={disabled}
                    onChange={(_, data) => onPtoChange(data.checked)}
                />
                <SettingsNumberInput
                    placeholder="Enter a value"
                    label="Daily PTO limit"
                    value={vacationLimit}
                    disabled={disabled || !ptoEnabled}
                    onChange={value => onChange({ vacationLimit: value })}
                />
                {ptoEnabled && vacationLimit !== null && vacationLimit !== '' && Number(vacationLimit) === 0 ? (
                    <span>PTO blocked</span>
                ) : null}
            </SettingRow>
        </>
    );
};
